/**
 * A mosque's own privacy page (personvern), as a file it can publish.  2026-10-14 (owner: «the mosque uploads, the One
 * Azan operator approves»).
 *
 * The page is served as it is at the mosque's /personvern (apps/api server.js), with a Content-Security-Policy that
 * allows no script, no frame, no form and nothing from elsewhere but pictures over https. It has to be ONE self-contained
 * HTML document, so these rules say so before it is published — the shell (`platform-admin privacy-file`), the mosque's
 * panel and the operators' console all ask THIS function, and nothing else decides what may be published:
 *
 *   - not empty, at most MAX_BYTES (300 kB: the largest real one, CJAS's, is about 20 kB);
 *   - UTF-8 text, no NUL;
 *   - a whole document: an <html> element and a <title> with words in it (the page's name in the browser tab, in the
 *     version history, and what an operator approves);
 *   - nothing that runs or takes the reader elsewhere: no <script>, no on…= handler, no javascript: address, no
 *     <iframe>/<frame>/<object>/<embed>, no <meta http-equiv="refresh">, no <base>, no <form>. The served page's CSP
 *     would block most of them anyway; refusing them up front means the page a person previews is the page visitors get.
 *
 * Pure: no I/O. The caller reads the bytes and fingerprints them.
 */
'use strict';

const MAX_BYTES = 300 * 1024;

/*
 * SI-02 (audit 2026-10-19): `<[a-z][^>]*\son…`, `<meta[^>]*http-equiv…` and
 * `<title[^>]*>…<\/title>` re-scanned the rest of the file from every opener
 * that had no `>` (or no closer) after it, and `\s*["']?\s*` split a long run
 * of spaces every possible way: 40 000 × `<a ` (120 kB) took 1.7 s, 300 kB
 * about 10 s, synchronously. The rules are the same; each tag is now found
 * once and the pattern only runs inside it.
 */

// Does `inner` match inside some tag opened by `open` — what /<open[^>]*inner/i
// matched? A tag runs to its first `>` (or to the end). Openers that start
// inside an earlier tag see only a suffix of its text, so after a tag that does
// not match, the search goes on after its `>`: every character is read once.
function inTag(text, open, inner) {
  const re = new RegExp(open, 'gi');
  for (let m; (m = re.exec(text));) {
    const gt = text.indexOf('>', m.index + m[0].length);
    if (inner.test(text.slice(m.index + m[0].length, gt < 0 ? text.length : gt))) return true;
    if (gt < 0) return false;
    re.lastIndex = gt + 1;
  }
  return false;
}

const ACTIVE = [
  [(h) => /<script[\s>/]/i.test(h), 'skript (<script>)'],
  [(h) => /<(iframe|frame|frameset|object|embed|applet)[\s>/]/i.test(h), 'innebygde sider eller programmer (<iframe>, <object>, <embed>)'],
  [(h) => inTag(h, '<meta', /http-equiv\s*=\s*(?:["']\s*)?refresh/i), 'automatisk videresending (<meta http-equiv="refresh">)'],
  [(h) => /<base[\s>/]/i.test(h), '<base>'],
  [(h) => /<form[\s>/]/i.test(h), 'skjemaer (<form>)'],
  [(h) => inTag(h, '<[a-z]', /\son[a-z]+\s*=/i), 'hendelseskode (on…=)'],
  // `\s*["']?\s*` → `\s*(?:["']\s*)?`: the same addresses, one way to read the spaces.
  [(h) => /(href|src|action|formaction|xlink:href)\s*=\s*(?:["']\s*)?javascript:/i.test(h), 'javascript:-adresser'],
];

/** The <title>'s words, whitespace folded, at most 200 characters; '' when there is none. */
function titleOf(text) {
  // /<title[^>]*>([\s\S]*?)<\/title>/i, without re-scanning from every <title.
  // If the first <title has no `>` or no </title> after it, no later one has.
  const s = String(text || '');
  const open = /<title/gi; const m = open.exec(s); if (!m) return '';
  const gt = s.indexOf('>', m.index + 6); if (gt < 0) return '';
  const close = /<\/title>/gi; close.lastIndex = gt + 1;
  const c = close.exec(s); if (!c) return '';
  return s.slice(gt + 1, c.index).replace(/\s+/g, ' ').trim().slice(0, 200);
}

/**
 * validate(input) → { ok: true, html, title, bytes } | { ok: false, code, message_no }
 *   input: a Buffer / Uint8Array (a file) or a string (a panel upload, already text).
 * Codes: empty, too_large, not_utf8, not_html, no_title, active_content.
 */
function validate(input) {
  let bytes; let html;
  if (typeof input === 'string') {
    html = input;
    bytes = new TextEncoder().encode(input).length;
  } else if (input && typeof input.length === 'number') {
    bytes = input.length;
    if (bytes > MAX_BYTES) return refuse('too_large');
    try { html = new TextDecoder('utf-8', { fatal: true }).decode(input); } catch { return refuse('not_utf8'); }
  } else return refuse('empty');
  if (!bytes || !html.trim()) return refuse('empty');
  if (bytes > MAX_BYTES) return refuse('too_large');
  if (html.includes('\u0000')) return refuse('not_html');
  if (!/<html[\s>]/i.test(html)) return refuse('not_html');
  const title = titleOf(html);
  if (!title) return refuse('no_title');
  const found = ACTIVE.filter(([test]) => test(html)).map(([, what]) => what);
  if (found.length) return { ok: false, code: 'active_content', found, message_no: `Siden kan ikke inneholde ${found.join(', ')}. Den skal være én selvstendig HTML-side uten kode som kjører.` };
  return { ok: true, html, title, bytes };
}

const MESSAGES = {
  empty: 'Filen er tom.',
  too_large: `Filen er for stor (over ${MAX_BYTES / 1024} kB). Siden skal være én selvstendig HTML-fil uten innebygde bilder i stor størrelse.`,
  not_utf8: 'Filen er ikke lagret som UTF-8-tekst. Lagre den som UTF-8 og prøv igjen.',
  not_html: 'Filen ser ikke ut som en HTML-side (den mangler <html>).',
  no_title: 'Siden mangler en tittel (<title>…</title>). Tittelen vises i nettleseren og i versjonshistorikken.',
};
function refuse(code) { return { ok: false, code, message_no: MESSAGES[code] }; }

module.exports = { MAX_BYTES, validate, titleOf };
